import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import {
  School as SchoolIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolvedIcon,
  PendingActions as PendingIcon,
  Reply as ReplyIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

const SchoolQuery = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [newQuery, setNewQuery] = useState({
    schoolName: "",
    contactPerson: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    priority: "medium",
  });
  const [replyMessage, setReplyMessage] = useState("");

  // Mock queries data - Replace with API call
  const queries = [
    {
      id: 1,
      schoolName: "Greenwood High School",
      contactPerson: "John Doe",
      email: "john@greenwood.edu",
      phone: "+91 98765 43210",
      subject: "License Renewal Query",
      message: "We need to renew our license. Please provide the renewal process details.",
      status: "Pending",
      priority: "High",
      category: "Billing",
      createdAt: "2024-01-15 10:30 AM",
      updatedAt: "2024-01-15 10:30 AM",
      assignedTo: "Admin",
      replies: [],
    },
    {
      id: 2,
      schoolName: "Sunshine Academy",
      contactPerson: "Jane Smith",
      email: "jane@sunshine.edu",
      phone: "+91 98765 43211",
      subject: "Feature Request",
      message: "Can we have a custom report feature for student attendance?",
      status: "In Progress",
      priority: "Medium",
      category: "Feature",
      createdAt: "2024-01-14 2:15 PM",
      updatedAt: "2024-01-15 9:00 AM",
      assignedTo: "TL",
      replies: [
        {
          id: 1,
          message: "We'll add this feature in the next update.",
          repliedBy: "Admin",
          repliedAt: "2024-01-15 9:00 AM",
        },
      ],
    },
    {
      id: 3,
      schoolName: "Riverside School",
      contactPerson: "Mike Johnson",
      email: "mike@riverside.edu",
      phone: "+91 98765 43212",
      subject: "Technical Support",
      message: "We're experiencing login issues. Please help.",
      status: "Resolved",
      priority: "High",
      category: "Technical",
      createdAt: "2024-01-13 11:20 AM",
      updatedAt: "2024-01-13 4:45 PM",
      assignedTo: "Support",
      replies: [
        {
          id: 1,
          message: "Issue has been resolved. Please try logging in again.",
          repliedBy: "Support Team",
          repliedAt: "2024-01-13 4:45 PM",
        },
      ],
    },
    {
      id: 4,
      schoolName: "Mountain View School",
      contactPerson: "Sarah Williams",
      email: "sarah@mountainview.edu",
      phone: "+91 98765 43213",
      subject: "Training Schedule",
      message: "When is the next training session scheduled?",
      status: "Pending",
      priority: "Low",
      category: "Training",
      createdAt: "2024-01-15 3:00 PM",
      updatedAt: "2024-01-15 3:00 PM",
      assignedTo: "Trainer",
      replies: [],
    },
  ];

  const filteredQueries = queries.filter((query) => {
    const matchesSearch =
      query.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      query.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      query.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      query.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || query.status === statusFilter;
    const matchesTab =
      selectedTab === 0 ||
      (selectedTab === 1 && query.status === "Pending") ||
      (selectedTab === 2 && query.status === "In Progress") ||
      (selectedTab === 3 && query.status === "Resolved");
    return matchesSearch && matchesStatus && matchesTab;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Resolved":
        return "success";
      case "In Progress":
        return "info";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Resolved":
        return <ResolvedIcon />;
      case "In Progress":
        return <PendingIcon />;
      case "Pending":
        return <PendingIcon />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "info";
      default:
        return "default";
    }
  };

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setQueryDialogOpen(true);
  };

  const handleReply = (query) => {
    setSelectedQuery(query);
    setReplyDialogOpen(true);
  };

  const handleSubmitReply = () => {
    if (!replyMessage.trim()) return;
    // Implement reply functionality
    console.log("Replying to query:", selectedQuery.id, replyMessage);
    setReplyDialogOpen(false);
    setReplyMessage("");
  };

  const handleCreateQuery = () => {
    // Implement create query functionality
    console.log("Creating query:", newQuery);
    setQueryDialogOpen(false);
    setNewQuery({
      schoolName: "",
      contactPerson: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      priority: "medium",
    });
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              School Queries
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage and respond to school inquiries and support requests
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setQueryDialogOpen(true)}
          >
            New Query
          </Button>
        </Box>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="All Queries" />
            <Tab label="Pending" />
            <Tab label="In Progress" />
            <Tab label="Resolved" />
          </Tabs>
        </Card>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search queries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Filter by Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Queries Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>School</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQueries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          No queries found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredQueries.map((query) => (
                      <TableRow key={query.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                              <SchoolIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {query.schoolName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {query.contactPerson}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                              <EmailIcon fontSize="small" sx={{ fontSize: "0.875rem" }} />
                              {query.email}
                            </Typography>
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <PhoneIcon fontSize="small" sx={{ fontSize: "0.875rem" }} />
                              {query.phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {query.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={query.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={query.priority}
                            size="small"
                            color={getPriorityColor(query.priority)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(query.status)}
                            label={query.status}
                            size="small"
                            color={getStatusColor(query.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{query.assignedTo}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {query.createdAt}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewQuery(query)}
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleReply(query)}
                              color="success"
                            >
                              <ReplyIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Query Details Dialog */}
        <Dialog open={queryDialogOpen} onClose={() => setQueryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedQuery ? "Query Details" : "Create New Query"}
          </DialogTitle>
          <DialogContent>
            {selectedQuery ? (
              <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      School Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedQuery.schoolName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Contact Person
                    </Typography>
                    <Typography variant="body1">{selectedQuery.contactPerson}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Subject
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedQuery.subject}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Message
                    </Typography>
                    <Typography variant="body1">{selectedQuery.message}</Typography>
                  </Grid>
                  {selectedQuery.replies && selectedQuery.replies.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1, mt: 2 }}>
                        Replies
                      </Typography>
                      {selectedQuery.replies.map((reply, index) => (
                        <Card key={index} sx={{ mb: 1, bgcolor: "background.default" }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2">{reply.message}</Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {reply.repliedBy} - {reply.repliedAt}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={newQuery.schoolName}
                    onChange={(e) => setNewQuery({ ...newQuery, schoolName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    value={newQuery.contactPerson}
                    onChange={(e) => setNewQuery({ ...newQuery, contactPerson: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={newQuery.email}
                    onChange={(e) => setNewQuery({ ...newQuery, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={newQuery.phone}
                    onChange={(e) => setNewQuery({ ...newQuery, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={newQuery.subject}
                    onChange={(e) => setNewQuery({ ...newQuery, subject: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={newQuery.message}
                    onChange={(e) => setNewQuery({ ...newQuery, message: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={newQuery.priority}
                      label="Priority"
                      onChange={(e) => setNewQuery({ ...newQuery, priority: e.target.value })}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setQueryDialogOpen(false); setSelectedQuery(null); }}>
              {selectedQuery ? "Close" : "Cancel"}
            </Button>
            {!selectedQuery && (
              <Button variant="contained" onClick={handleCreateQuery}>
                Create Query
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reply to Query</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Reply Message"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setReplyDialogOpen(false); setReplyMessage(""); }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmitReply}>
              Send Reply
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default SchoolQuery;

